import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ApiError } from "@atlas/shared";
import { ArrowLeft, ArrowUpRight, Layers } from "lucide-react";
import { fetchGroup, fetchGroupCollectionBoard } from "../../api/groups";
import { EmptyState } from "../../components/EmptyState";
import { Skel } from "../../components/Skeleton";
import { usePageMeta } from "../../hooks/usePageMeta";
import { COLLECTION_COUNT_RULE, GroupNotFound } from "./GroupPage";
import {
  CARD_STYLE,
  CoverHero,
  GroupsPageFrame,
  HERO_BACK_STYLE,
  HERO_CONTROL_STYLE,
  HERO_EYEBROW_STYLE,
  HERO_TEXT_SOFT,
  HERO_TITLE_STYLE,
  HeroBackLabel,
  SectionHeading,
  Standings,
  fmtNumber,
  groupErrorMessage,
} from "./group-ui";

// /groups/:id/collections/:slug (ATL-151): one collection's all-time board for
// the group. Every active member is listed; members on zero sit unranked at
// the bottom.

export default function GroupCollectionPage() {
  const { id, slug } = useParams({ strict: false }) as { id: string; slug: string };

  const boardQuery = useQuery({
    queryKey: ["groups", id, "collections", slug],
    queryFn: () => fetchGroupCollectionBoard(id, slug),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });
  const groupQuery = useQuery({
    queryKey: ["groups", id],
    queryFn: () => fetchGroup(id),
    retry: false,
  });

  usePageMeta({
    title: "Group collection - Atlas",
    description: "A collection board in a private Atlas group.",
    canonicalPath: "/groups",
    noindex: true,
  });

  const groupName = groupQuery.data?.group.name ?? "Group";
  const backLink = (
    <Link
      to="/groups/$id"
      params={{ id }}
      className="eyebrow"
      style={{ fontSize: 10, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0 }}
    >
      <ArrowLeft size={10} /> <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{groupName}</span>
    </Link>
  );

  const board404 = boardQuery.error instanceof ApiError && boardQuery.error.status === 404;
  // On a board 404, wait for the group so a missing collection isn't shown as a missing group.
  if (boardQuery.isLoading || (board404 && groupQuery.isLoading)) {
    return (
      <GroupsPageFrame>
        <Skel height="200px" radius="var(--radius-xl)" />
        <Skel height="300px" radius="var(--radius-lg)" />
      </GroupsPageFrame>
    );
  }
  if (boardQuery.isError || !boardQuery.data) {
    // A 404 with the group itself still loading fine means the collection
    // isn't one with a board (unknown slug, or a single-trail collection).
    if (board404 && groupQuery.data) {
      return (
        <GroupsPageFrame>
          {backLink}
          <EmptyState
            icon={<Layers size={20} />}
            title="No board for this collection"
            hint="It may have been renamed, or it is a single trail, which has its own page instead."
          />
        </GroupsPageFrame>
      );
    }
    if (board404) {
      return <GroupNotFound error={groupQuery.error ?? boardQuery.error} />;
    }
    return (
      <GroupsPageFrame>
        {backLink}
        <EmptyState
          icon={<Layers size={20} />}
          title="Couldn't load this board"
          hint={groupErrorMessage(boardQuery.error, "Try again in a moment.")}
        />
      </GroupsPageFrame>
    );
  }

  const { collection, rows } = boardQuery.data;
  const ranked = rows.filter((r) => r.rank != null).length;

  return (
    <GroupsPageFrame>
      <CoverHero
        compact
        images={collection.coverImage ? [collection.coverImage] : []}
        top={
          <>
            <Link to="/groups/$id" params={{ id }} style={HERO_BACK_STYLE}>
              <HeroBackLabel label={groupName} />
            </Link>
            <Link to="/collections/$slug" params={{ slug: collection.slug }} style={HERO_CONTROL_STYLE}>
              View the collection <ArrowUpRight size={14} />
            </Link>
          </>
        }
      >
        <p className="tnum" style={HERO_EYEBROW_STYLE}>
          Collection board · {fmtNumber(collection.total)} to collect
        </p>
        <h1 style={HERO_TITLE_STYLE}>{collection.name}</h1>
        <p style={{ fontSize: 14, color: HERO_TEXT_SOFT, margin: 0, lineHeight: 1.5, textShadow: "0 1px 8px rgba(0,0,0,.35)" }}>
          {COLLECTION_COUNT_RULE}
        </p>
      </CoverHero>

      <section style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
        <SectionHeading
          title="Standings"
          hint={ranked > 0 ? `${fmtNumber(ranked)} of the group on the board.` : undefined}
        />
        <div style={{ ...CARD_STYLE, padding: 14 }}>
          {ranked === 0 ? (
            <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>
              Nobody in {groupName === "Group" ? "the group" : groupName} has anything from {collection.name} yet.
            </p>
          ) : (
            <Standings rows={rows} unit="" total={collection.total} />
          )}
        </div>
      </section>
    </GroupsPageFrame>
  );
}
